import React from 'react';
import { Gem, Medal, Shield, Award } from 'lucide-react';

/**
 * Maps a membership plan to a visual tier using DCCF plan names.
 * Hierarchy: Executive (highest) → Premier → Essential → Hybrid (entry)
 */
const getTierFromPlan = (plan) => {
  if (!plan) return null;
  const name = (plan.name || '').toLowerCase();
  const priceStr = String(plan.price || '').replace(/[^0-9.]/g, '');
  const price = parseFloat(priceStr) || 0;

  if (name.includes('executive')) return 'executive';
  if (name.includes('premier')) return 'premier';
  if (name.includes('essential')) return 'essential';
  if (name.includes('hybrid')) return 'hybrid';

  // Price fallback
  if (price >= 800) return 'executive';
  if (price >= 400) return 'premier';
  if (price >= 200) return 'essential';
  return 'hybrid';
};

const tierConfig = {
  executive: {
    label: 'Executive',
    icon: Gem,
    text: 'text-[#2A5F7A]',
    bg: 'bg-gradient-to-br from-[#E8F4FA] to-[#D0E8F2]',
    border: 'border-[#A8C8DB]',
    shadow: 'shadow-[0_2px_12px_rgba(42,95,122,0.15)]',
    glow: 'ring-1 ring-[#B8D4E3]/50',
    iconColor: 'text-[#4A9BC5]',
  },
  premier: {
    label: 'Premier',
    icon: Medal,
    text: 'text-[#7A5C1F]',
    bg: 'bg-gradient-to-br from-[#FFF8E7] to-[#F5E6C0]',
    border: 'border-[#D4A853]',
    shadow: 'shadow-[0_2px_12px_rgba(212,168,83,0.2)]',
    glow: 'ring-1 ring-[#D4A853]/40',
    iconColor: 'text-[#B8922E]',
  },
  essential: {
    label: 'Essential',
    icon: Shield,
    text: 'text-[#455A64]',
    bg: 'bg-gradient-to-br from-[#F5F7F9] to-[#E8ECEF]',
    border: 'border-[#B0BEC5]',
    shadow: 'shadow-[0_2px_12px_rgba(69,90,100,0.12)]',
    glow: 'ring-1 ring-[#B0BEC5]/40',
    iconColor: 'text-[#78909C]',
  },
  hybrid: {
    label: 'Hybrid',
    icon: Award,
    text: 'text-[#6D4426]',
    bg: 'bg-gradient-to-br from-[#FDF5EE] to-[#F0DCC8]',
    border: 'border-[#C68B5E]',
    shadow: 'shadow-[0_2px_12px_rgba(184,115,51,0.15)]',
    glow: 'ring-1 ring-[#C68B5E]/30',
    iconColor: 'text-[#B87333]',
  },
};

/**
 * Full-size membership seal — for Business Profile header.
 */
export const MembershipSeal = ({ plan, className = '' }) => {
  const tier = getTierFromPlan(plan);
  if (!tier) return null;
  const cfg = tierConfig[tier];
  const Icon = cfg.icon;

  return (
    <div className={`flex flex-col items-center ${className}`} data-testid={`membership-seal-${tier}`}>
      <div className={`relative w-20 h-20 sm:w-24 sm:h-24 rounded-full ${cfg.bg} ${cfg.border} border-2 ${cfg.shadow} ${cfg.glow} flex items-center justify-center`}>
        <div className="absolute inset-1 rounded-full border border-dashed opacity-30" style={{ borderColor: 'currentColor' }} />
        <Icon className={`w-8 h-8 sm:w-10 sm:h-10 ${cfg.iconColor}`} strokeWidth={1.5} />
      </div>
      <div className="mt-2 text-center">
        <p className={`text-[11px] sm:text-xs font-bold ${cfg.text} uppercase tracking-wider`}>{cfg.label}</p>
        <p className="text-[9px] sm:text-[10px] text-slate-400 font-medium">Member</p>
      </div>
    </div>
  );
};

/**
 * Compact badge — for directory cards & inline use.
 */
export const MembershipBadge = ({ plan, className = '' }) => {
  const tier = getTierFromPlan(plan);
  if (!tier) return null;
  const cfg = tierConfig[tier];
  const Icon = cfg.icon;

  return (
    <span className={`inline-flex items-center gap-1 ${cfg.bg} ${cfg.text} ${cfg.border} border text-[10px] sm:text-[11px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap ${className}`}
      data-testid={`membership-badge-${tier}`}>
      <Icon className="w-3 h-3" strokeWidth={2} />
      {cfg.label}
    </span>
  );
};

export { getTierFromPlan, tierConfig };
